/**
 * @email kpozos@ibl.mx
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer
 * @hook TotalMealsCardsApp
 * @description Custom hook to manage state and logic for the Total Meals system.
 * It handles fetching KPIs and statistics related to meals, prepares result data with
 * randomized default icons, and exposes helper functions for use in UI components.
 *
 * @param {Object} params - Hook parameters.
 * @param {string} params.selectedOption - **Required.** Determines the search type for meals (e.g., daily, weekly, monthly).
 *
 * @returns {Object} Hook state and utilities.
 * @returns {Function} return.t - Translation function from i18next.
 * @returns {Array<Object>} return.results - Array of meal results enriched with randomized default icons.
 * @returns {Function} return.getDefaultImage - Function that returns a random default meal icon.
 *
 * @example
 * const { t, results, getDefaultImage } = TotalMealsCardsApp({ selectedOption: "daily" });
 *
 */
import React, {useEffect, useState} from 'react';
import { useDispatch } from 'react-redux';
import {useTranslation} from "react-i18next";
import {GetMealTimeStats, GetTotalMealsKpisGrid} from "../../../../../reduxToolkit/FoodieOrders/FoodieOrdersApi";
import {ATTENDANCE_MODES} from "../Models/Model";
import GetDateFilter from "../../../Company/Payroll/Functionality/GetDateFilter";
import {
    DefaultMealImageRiceMeal,
    DefaultMealImageRiceEggMeal,
    DefaultMealImageVegetableMeal,
    DefaultMealImageFishMeal, DefaultMealImageSandwichMeal, DefaultMealImageFruitsMeal
} from "../../../../../components/buttons/IconButtons";

const TotalMealsCardsApp = ({ selectedOption }) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const { getAttendanceDate } = GetDateFilter();
    const [results, setResults] = useState([]); // Store the search results

    /**
     * useEffect lifecycle hook
     * @description Runs whenever `selectedOption` changes.
     * It fetches the KPI grid and meal statistics from the backend,
     * then enriches the results with a randomly assigned default image.
     */
    useEffect(() => {
        let dates = getAttendanceDate(ATTENDANCE_MODES.daily); // Obtain current date; supports daily/weekly/monthly modes
        let body = {
            attendanceSearchType: selectedOption,
            date: dates.start
        };

        // Dispatch request to populate KPI cards
        dispatch(GetTotalMealsKpisGrid(body));

        // Dispatch request to populate statistics by meal time
        dispatch(GetMealTimeStats(body)).then(({ payload:{ data: { data, success }}}) => {
            if(success){
                let aux = [...data];
                // Append a randomized image for each record
                setResults(aux.map(e => ({...e, image: getDefaultImage()})));
            }
        });
    }, [selectedOption]);

    /**
     * @function getDefaultImage
     * @description Returns a random default meal image icon.
     * It shuffles the available image components and selects one at random.
     *
     * @returns {JSX.Element} A randomly chosen meal icon component.
     *
     * @example
     * const icon = getDefaultImage();
     * return <div>{icon}</div>;
     */
    const getDefaultImage = () => {
        let size = 100;
        const images = [
            <DefaultMealImageRiceMeal height={size} width={size}/>,
            <DefaultMealImageRiceEggMeal height={size} width={size}/>,
            <DefaultMealImageVegetableMeal height={size} width={size}/>,
            <DefaultMealImageFishMeal height={size} width={size}/>,
            <DefaultMealImageSandwichMeal height={size} width={size}/>,
            <DefaultMealImageFruitsMeal height={size} width={size}/>
        ];

        const arr = [...images];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }

        const randomIndex = Math.floor(Math.random() * arr.length);
        return arr[randomIndex];
    }

    return {
        t,
        results,
        setResults,
        getDefaultImage
    };
};

export default TotalMealsCardsApp;
